/**
 * PDE-2621 — Card 2 sunset lifecycle utility (FE-owned per-SKU suppression; reused by Card 4).
 * Cross-platform sibling of iOS PDE-2624 / Android PDE-2627 — state machine, key schema, and
 * default windows are byte-locked across all three (see PDE-2624 plan ADR-6 / ADR-7).
 *
 * Client-side, per-SKU display-lifecycle gating backed by localStorage. The backend returns a
 * ranked list of eligible SKUs; this module selects the first non-suppressed SKU for display and
 * hides the card entirely when every SKU is suppressed. State is keyed per signed-in customer ×
 * skuId with a card `prefix` so sibling cards and sibling users reuse it in isolation.
 *
 * localStorage key: `{prefix}{profileId}_{skuId}` (e.g. `puwylo_card_deals_cust789_2145678`)
 * storing `{ firstShown, lastClicked?, suppressedUntil? }` (all ISO strings). `suppressedUntil` is
 * anchored once to `firstShown + suppressionWindow` and is never recomputed/slid.
 *
 * Multi-user isolation (ADR-7): Card 2 is logged-in-only; keys embed the signed-in `profileId`
 * so one customer's timers never leak into another customer's session on a shared device. When no
 * customer is signed in (`profileId` falsy) the helper no-ops: mutators write nothing and `gate`
 * returns SHOW (defensive — the consumer must guard rendering on auth state).
 *
 * Eligibility is backend-owned (ADR-6): the FE does NOT track purchase or OOS. When a SKU is
 * bought or goes OOS the backend drops it from the ranked list; its orphaned local state ages out
 * via cleanup's single `firstShown + suppressionWindow` TTL. There is no recordPurchasedOrOOS API.
 *
 * State machine (first match wins):
 *   R1 suppressedUntil > now                          -> HIDE
 *   R2 lastClicked set  AND now - lastClicked >= 24h  -> HIDE + set suppressedUntil = firstShown + 15d
 *   R3 lastClicked unset AND now - firstShown  >= 5d  -> HIDE + set suppressedUntil = firstShown + 15d
 *   R4 else                                           -> SHOW
 *
 * Windows come from the Config Hub via sunsetConfig (read at call-time). On the client, a failed or
 * unavailable localStorage (quota / private mode) degrades to an in-memory session Map so the module
 * never throws. During SSR (`Sephora.isNodeRender`) every storage access is a hard no-op — the
 * module-level Map is a per-process singleton shared across requests, so touching it server-side would
 * leak per-user state across requests; the helper behaves as "no local state" and defers to the client.
 */

import Storage from 'utils/localStorage/Storage';
import dateUtils from 'utils/Date';
import userUtils from 'utils/User';
import { getWindows } from 'utils/puwyloSunset/sunsetConfig';

/** Sunset gate verdicts. Consuming cards compare against these instead of raw strings. */
const SHOW = 'SHOW';
const HIDE = 'HIDE';

/** Canonical per-card localStorage key prefixes. */
const PREFIX_CARD_DEALS = 'puwylo_card_deals_';
const PREFIX_CARD_FOLLOWON = 'puwylo_card_followon_';

// In-memory fallback used when localStorage is unavailable (SSR-degraded / quota-exceeded). Keyed
// identically to localStorage so reads/writes stay consistent across the two backing stores.
const memoryStore = new Map();

// Injected clock (test-only override via __setClock). Defaults to the real wall clock.
let clock = () => new Date();

/** @param {() => Date} fn */
const __setClock = fn => {
    clock = fn;
};

/** Test-only: restore the real wall clock. */
const __resetClock = () => {
    clock = () => new Date();
};

// Injected identity resolver (test-only override via __setIdentity). Defaults to the signed-in
// customer profileId (ADR-7). A falsy id (anon / mid-login) makes every entry point a no-op.
let resolveProfileId = () => userUtils.getProfileId();

/** @param {() => (string|null|undefined)} fn */
const __setIdentity = fn => {
    resolveProfileId = fn;
};

/** Test-only: restore the default identity resolver (userUtils.getProfileId). */
const __resetIdentity = () => {
    resolveProfileId = () => userUtils.getProfileId();
};

/**
 * Test-only: clear the in-memory fallback Map between specs. The Map is a module-level singleton and
 * is not reset by clearing window.localStorage, so specs must reset it explicitly.
 */
const __resetMemory = () => {
    memoryStore.clear();
};

const nowMs = () => clock().getTime();
const nowIso = () => dateUtils.getShortenedISOString(clock());
const isoAt = ms => dateUtils.getShortenedISOString(new Date(ms));

// Effective key = `{prefix}{profileId}_{skuId}` — byte-locked with iOS PDE-2624 / Android PDE-2627.
const keyOf = (prefix, profileId, skuId) => `${prefix}${profileId}_${skuId}`;

// True during server-side render. `memoryStore` is a module-level singleton shared across every SSR
// request in the Node process; consulting or populating it server-side would leak per-user
// (profileId-keyed) state across requests and grow unbounded. All storage access is therefore a hard
// no-op on SSR — the helper behaves as "no local state" (SHOW) and defers real evaluation to the
// client after hydration.
const isNodeRender = () => typeof Sephora !== 'undefined' && Sephora.isNodeRender;

/**
 * Read a per-SKU entry. Prefers localStorage; on throw or miss, consults the in-memory Map so
 * quota/SSR-degraded sessions still resolve state written during this session.
 * @param {string} key
 * @returns {Object|null}
 */
const readEntry = key => {
    // SSR hard no-op: never touch storage or the shared session Map (cross-request safety).
    if (isNodeRender()) {
        return null;
    }

    // The in-memory Map, when it holds this key, is the most recent write that localStorage could not
    // persist (silent quota / private-mode failure). Prefer it so a stale value still sitting in
    // localStorage under the same key is never returned in place of the newer session state.
    if (memoryStore.has(key)) {
        return memoryStore.get(key);
    }

    try {
        const data = Storage.local.getItem(key);

        if (data !== null && data !== undefined) {
            return data;
        }
    } catch {
        // localStorage unavailable — treat as no state.
    }

    return null;
};

/**
 * Persist a per-SKU entry, guaranteeing the state survives for the session even when localStorage
 * can't hold it.
 *
 * `Storage.local.setItem` swallows quota/private-mode errors internally (see
 * utils/localStorage/Storage.js), so a failed write throws nothing. We detect it with a
 * read-after-write that compares the exact value: a bare non-null check would be fooled by a
 * pre-existing (stale) value when updating a key. On a confirmed persist we drop any Map copy; on a
 * failed persist we hold the newest entry in the Map, and `readEntry` prefers the Map so the stale
 * localStorage value is never returned in its place.
 * @param {string} key
 * @param {Object} entry
 */
const writeEntry = (key, entry) => {
    // SSR hard no-op: do not persist to storage or the shared session Map (see isNodeRender).
    if (isNodeRender()) {
        return;
    }

    let persisted = false;

    try {
        Storage.local.setItem(key, entry);
        // setItem swallows quota/private-mode errors internally, so a failed write throws nothing.
        // Confirm the EXACT value stuck by reading it back and comparing — a pre-existing (stale) value
        // under this key would satisfy a mere non-null check and mask the loss when updating a key.
        persisted = JSON.stringify(Storage.local.getItem(key)) === JSON.stringify(entry);
    } catch {
        persisted = false;
    }

    if (persisted) {
        memoryStore.delete(key);
    } else {
        memoryStore.set(key, entry);
    }
};

/**
 * Remove a per-SKU entry from both backing stores.
 * @param {string} key
 */
const clearEntry = key => {
    // SSR hard no-op: nothing is ever written server-side, so nothing to clear.
    if (isNodeRender()) {
        return;
    }

    try {
        Storage.local.removeItem(key);
    } catch {
        // localStorage unavailable — nothing to remove there.
    }

    memoryStore.delete(key);
};

/**
 * Single-SKU verdict (convenience wrapper). Delegates to `gateWithReason` and returns only
 * the verdict string, discarding the trigger. Callers that need the suppression reason
 * (e.g. SOT sunset-suppressed event payloads) should use `gateWithReason` directly.
 * @param {string} skuId
 * @param {string} prefix card namespace (e.g. PREFIX_CARD_DEALS)
 * @returns {'SHOW'|'HIDE'}
 */
const gate = (skuId, prefix) => gateWithReason(skuId, prefix).verdict;

/**
 * Select the first non-suppressed SKU from an ordered list, starting the suppression clock only for
 * the SKU actually shown. Prunes stale entries on read (AC11). Returns `null` when every SKU is
 * suppressed (card hidden entirely — AC7).
 * @param {Array<string>} skuList ordered list of skuIds
 * @param {string} prefix card namespace
 * @returns {string|null}
 */
const getDisplaySku = (skuList, prefix) => {
    cleanup(prefix);

    for (const skuId of skuList) {
        if (gate(skuId, prefix) === SHOW) {
            recordShown(skuId, prefix);

            return skuId;
        }
    }

    return null;
};

/**
 * Record that a SKU was shown, setting `firstShown` on the first render only (idempotent — the
 * suppression window is anchored to the original firstShown).
 * @param {string} skuId
 * @param {string} prefix card namespace
 */
const recordShown = (skuId, prefix) => {
    const profileId = resolveProfileId();

    if (!profileId) {
        return;
    }

    const key = keyOf(prefix, profileId, skuId);
    const entry = readEntry(key);

    if (entry && entry.firstShown) {
        return;
    }

    writeEntry(key, { firstShown: nowIso() });
};

/**
 * Record a SKU click, setting `lastClicked` to now on the first tap only. Repeat taps preserve the
 * original click timestamp so the click-hide timer runs from the first interaction (AC10).
 * @param {string} skuId
 * @param {string} prefix card namespace
 */
const recordClicked = (skuId, prefix) => {
    const profileId = resolveProfileId();

    if (!profileId) {
        return;
    }

    const key = keyOf(prefix, profileId, skuId);
    const entry = readEntry(key) || {};

    if (!entry.firstShown) {
        entry.firstShown = nowIso();
    }

    if (!entry.lastClicked) {
        entry.lastClicked = nowIso();
    }

    writeEntry(key, entry);
};

/**
 * Evict entries whose suppression window is over (`firstShown + suppressionWindow <= now`), scoped to
 * the given prefix only. Applies to both backing stores (AC11).
 * @param {string} prefix card namespace
 */
const cleanup = prefix => {
    // SSR hard no-op: no client storage to prune, and the shared Map must not be touched server-side.
    if (isNodeRender()) {
        return;
    }

    const { suppressionWindow } = getWindows();
    const now = nowMs();
    const isElapsed = entry => {
        if (!entry) {
            return false;
        }

        const firstShownMs = Date.parse(entry.firstShown);

        // Missing / unparseable firstShown -> corrupt entry; evict it too (it can never age out
        // normally because NaN comparisons are always false).
        return Number.isNaN(firstShownMs) || now >= firstShownMs + suppressionWindow;
    };

    try {
        Object.keys(window.localStorage)
            .filter(key => key.startsWith(prefix))
            .forEach(key => {
                if (isElapsed(readEntry(key))) {
                    clearEntry(key);
                }
            });
    } catch {
        // localStorage unavailable — nothing to prune there.
    }

    memoryStore.forEach((entry, key) => {
        if (key.startsWith(prefix) && isElapsed(entry)) {
            memoryStore.delete(key);
        }
    });
};

/**
 * Single-SKU verdict with suppression reason. Applies the suppression rules in priority order
 * (first match wins). Rules 2/3 anchor `suppressedUntil` to `firstShown + suppressionWindow`
 * once; Rule 1 short-circuits before any recompute. Never mutates `lastClicked` (a cart-add
 * must not restart the click timer — AC5). Returns SHOW with no state when no customer is
 * signed in (ADR-7 anon no-op).
 *
 * This is the single source of truth for the sunset state machine — `gate()` delegates here.
 *
 * @param {string} skuId
 * @param {string} prefix card namespace (e.g. PREFIX_CARD_DEALS)
 * @returns {{ verdict: 'SHOW'|'HIDE', trigger?: string }}
 *   `trigger` is present only on HIDE verdicts:
 *   - `'suppressed_until'` — R1: still within an anchored suppression window
 *   - `'24h_post_click'`   — R2: clicked and past the click-hide window
 *   - `'5d_no_click'`      — R3: never clicked and past the no-click-hide window
 */
const gateWithReason = (skuId, prefix) => {
    const profileId = resolveProfileId();

    if (!profileId) {
        return { verdict: SHOW };
    }

    const key = keyOf(prefix, profileId, skuId);
    const entry = readEntry(key);

    if (!entry) {
        return { verdict: SHOW };
    }

    // Corrupt / foreign blob guard: firstShown anchors every window and the suppressedUntil math.
    // If it is missing or unparseable, Date.parse -> NaN and isoAt(NaN) would throw "Invalid time
    // value" (RangeError) inside Rule 2, crashing the render. Drop the bad entry and treat the SKU
    // as fresh so it recovers on the next recordShown.
    if (Number.isNaN(Date.parse(entry.firstShown))) {
        clearEntry(key);

        return { verdict: SHOW };
    }

    // Corrupt lastClicked guard: a truthy-but-unparseable timestamp makes Rule 2's Date.parse -> NaN
    // (so R2 never fires) while Rule 3 is skipped because lastClicked is truthy — wedging the SKU on
    // SHOW until cleanup. Treat it as corrupt and recover fresh.
    if (entry.lastClicked && Number.isNaN(Date.parse(entry.lastClicked))) {
        clearEntry(key);

        return { verdict: SHOW };
    }

    const now = nowMs();

    // R1 — still within an anchored suppression window; never recompute.
    if (entry.suppressedUntil && Date.parse(entry.suppressedUntil) > now) {
        return { verdict: HIDE, trigger: 'suppressed_until' };
    }

    const { clickWindow, noClickWindow, suppressionWindow } = getWindows();

    // R2 — clicked and past the click-hide window.
    if (entry.lastClicked && now - Date.parse(entry.lastClicked) >= clickWindow) {
        entry.suppressedUntil = isoAt(Date.parse(entry.firstShown) + suppressionWindow);
        writeEntry(key, entry);

        return { verdict: HIDE, trigger: '24h_post_click' };
    }

    // R3 — never clicked and past the no-click-hide window.
    if (!entry.lastClicked && entry.firstShown && now - Date.parse(entry.firstShown) >= noClickWindow) {
        entry.suppressedUntil = isoAt(Date.parse(entry.firstShown) + suppressionWindow);
        writeEntry(key, entry);

        return { verdict: HIDE, trigger: '5d_no_click' };
    }

    // R4 — recently shown / within all windows.
    return { verdict: SHOW };
};

export {
    SHOW,
    HIDE,
    PREFIX_CARD_DEALS,
    PREFIX_CARD_FOLLOWON,
    gate,
    gateWithReason,
    getDisplaySku,
    recordShown,
    recordClicked,
    cleanup,
    __setClock,
    __resetClock,
    __setIdentity,
    __resetIdentity,
    __resetMemory
};
